/**
 * 文字の大きさの設定
 * 端末ごとに選んだ段階を保存し、<html>のCSS変数へ倍率を渡して全画面の文字を拡大します。
 * 余白やボタンの大きさは変えず、文字だけを大きくします（tailwind.config.jsとWebPrimitivesのnormalizeStyleが倍率を読む）。
 */

import webStorage from './webStorage';
import { logWarn } from '../utils/logger';
import { TEXT_SCALE_CSS_VARIABLE } from '../utils/textScale';

/** 文字の大きさの段階（表示順）。倍率と表示名はここだけで定義する */
export const TEXT_SIZE_LEVELS = [
  { id: 'standard', label: '標準', scale: 1 },
  { id: 'large', label: '大きめ', scale: 1.15 },
  { id: 'xlarge', label: 'とても大きい', scale: 1.3 },
] as const;

/** 文字の大きさの段階ID */
export type TextSizeLevelId = (typeof TEXT_SIZE_LEVELS)[number]['id'];

/** 端末に保存するキー */
const TEXT_SIZE_STORAGE_KEY = '@ikomasai/text_size';
/** 保存値が無い・読めない場合の段階 */
const DEFAULT_TEXT_SIZE_LEVEL_ID: TextSizeLevelId = 'standard';

/** 全画面で共有する現在の段階 */
let currentLevelId: TextSizeLevelId = DEFAULT_TEXT_SIZE_LEVEL_ID;
/** Reactの購読先 */
const listeners = new Set<() => void>();

/**
 * 保存値を段階IDとして読む
 * @param {unknown} rawValue - 端末に保存されていた値
 * @returns {TextSizeLevelId | null} 段階ID。知らない値はnull
 */
const parseTextSizeLevelId = (rawValue: unknown): TextSizeLevelId | null =>
  TEXT_SIZE_LEVELS.find((level) => level.id === rawValue)?.id ?? null;

/**
 * 段階を<html>の倍率へ反映し、購読先へ知らせる。<html>へ書き込むのはこの関数だけ
 * @param {TextSizeLevelId} levelId - 段階ID
 * @returns {void}
 */
const applyTextSizeLevel = (levelId: TextSizeLevelId) => {
  const level = TEXT_SIZE_LEVELS.find(({ id }) => id === levelId) ?? TEXT_SIZE_LEVELS[0];
  currentLevelId = level.id;
  document.documentElement.style.setProperty(TEXT_SCALE_CSS_VARIABLE, String(level.scale));
  listeners.forEach((listener) => listener());
};

/**
 * 起動時に保存値を読んで倍率を設定する。失敗してもrejectせず、標準で表示する
 * @returns {Promise<void>} 設定の完了
 */
export const initTextSizePreference = async (): Promise<void> => {
  try {
    const savedValue = await webStorage.getItem(TEXT_SIZE_STORAGE_KEY);
    const savedLevelId = parseTextSizeLevelId(savedValue);
    if (savedValue !== null && !savedLevelId) {
      logWarn('保存されている文字の大きさを読めないため、標準で表示します。', { savedValue });
    }
    applyTextSizeLevel(savedLevelId ?? DEFAULT_TEXT_SIZE_LEVEL_ID);
  } catch (error) {
    logWarn('文字の大きさの設定を読み込めないため、標準で表示します。', {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    applyTextSizeLevel(DEFAULT_TEXT_SIZE_LEVEL_ID);
  }
};

/**
 * 段階をすぐに反映して保存する。保存に失敗しても、この表示中は選んだ段階のままにする
 * @param {TextSizeLevelId} levelId - 段階ID
 * @returns {void}
 */
export const setTextSizeLevel = (levelId: TextSizeLevelId): void => {
  applyTextSizeLevel(levelId);
  webStorage.setItem(TEXT_SIZE_STORAGE_KEY, levelId).catch((error) => {
    logWarn('文字の大きさを端末に保存できませんでした。この表示中は選んだ大きさのままです。', {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  });
};

/**
 * 画面から現在の段階を購読する
 * @param {() => void} listener - Reactの更新通知
 * @returns {() => void} 購読解除
 */
export const subscribeTextSizeLevel = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * 現在の段階を返す
 * @returns {TextSizeLevelId} 段階ID
 */
export const getTextSizeLevelSnapshot = (): TextSizeLevelId => currentLevelId;
